'use client';
import React from 'react';
import { ImageTextImageProps } from '@/components/image-text/types';
import { Badge } from '@/components/badge/Badge';
import { Link } from '@/components/link/Link';
import { BaseImage } from '@/components/baseImage/BaseImage';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import { AccordionList } from '@/components/accordion/AccordionList';
import clsx from 'clsx';
import { imageTextThemeClasses } from '@/components/image-text/theme';
import { ImageLegend } from '@/components/image-text/ImageLegend';

export const ImageAndText: React.FC<ImageTextImageProps> = ({
  title,
  description,
  badge,
  link,
  image,
  legend,
  imageBorder,
  sectionId,
  accordionList,
  reverse,
  theme,
  tag,
  prerender,
}) => {
  const fallbackTheme = theme ?? 'light';
  const isDark = fallbackTheme === 'dark';
  const hasAccordion = Boolean(accordionList?.length);
  const legendRows = legend ?? [];
  // An annotated image reads as one object: artwork and key inside a single
  // card. Without a legend the image stays exactly as it was.
  const hasLegend = legendRows.length > 0;
  return (
    <div
      id={sectionId}
      className={clsx('py-10', imageTextThemeClasses.root(fallbackTheme))}
    >
      <div
        className={clsx(
          'container mx-auto flex gap-14 lg:gap-40',
          { 'flex-col-reverse lg:flex-row-reverse': reverse },
          { 'flex-col lg:flex-row': !reverse },
        )}
      >
        <div
          className={clsx(
            'flex w-full flex-1 flex-col py-4 md:py-0',
            !hasAccordion && 'md:justify-center',
          )}
        >
          <div className="flex w-full flex-col gap-6">
            <div className="flex flex-col gap-6 lg:gap-4">
              <div className="flex flex-col gap-3">
                {badge && <Badge {...badge} />}
                <h2
                  className={`hyphens-auto break-words text-2xl font-bold leading-tight ${imageTextThemeClasses.title(
                    fallbackTheme,
                  )}`}
                >
                  {title}
                </h2>
              </div>
              {description && (
                <div
                  className={clsx(
                    'col-span-full text-base',
                    isDark ? 'lg:col-start-6 lg:col-end-13' : 'font-medium',
                    imageTextThemeClasses.description(fallbackTheme),
                  )}
                >
                  <SerializeDescription description={description} />
                </div>
              )}
              {hasAccordion ? (
                <div
                  className={clsx('mt-3 flex w-full flex-col gap-3 lg:w-[90%]')}
                >
                  {accordionList?.map((accordion, index) => (
                    <AccordionList
                      key={index}
                      {...accordion}
                      selectedTag={tag}
                      theme={fallbackTheme}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            {link && (
              <div className="split-image-hero__link w-fit">
                <Link {...link} className="px-0" />
              </div>
            )}
          </div>
        </div>
        <div className="w-full flex-1">
          <div
            className={clsx('h-max w-full rounded-lg', {
              'bg-light-blue p-7': imageBorder,
            })}
          >
            {hasLegend ? (
              <div
                className={clsx(
                  'overflow-hidden rounded-xl border',
                  imageTextThemeClasses.legendCard(fallbackTheme),
                )}
              >
                <div className="p-2">
                  <BaseImage
                    {...image}
                    prerender={prerender}
                    className="w-full overflow-hidden rounded-lg"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
                <ImageLegend items={legendRows} theme={fallbackTheme} />
              </div>
            ) : (
              <BaseImage
                {...image}
                prerender={prerender}
                className="h-full w-full overflow-hidden rounded-lg py-12"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
